import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePricingTiers, useUsage } from '@/hooks/useUsage'

interface UpgradeModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function formatLimit(value: number | null): string {
  if (value === null) return 'Unlimited'
  return value.toLocaleString()
}

export function UpgradeModal({ open, onOpenChange }: UpgradeModalProps) {
  const { data: tiers = [] } = usePricingTiers()
  const { data: usage } = useUsage()

  const currentTierName = usage?.tier.name

  // Filter out beta tier from display, show free/pro/studio
  const displayTiers = [...tiers.filter(t => !t.is_beta)].sort((a, b) => a.sort_order - b.sort_order)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upgrade Your Plan</DialogTitle>
          <DialogDescription>
            {currentTierName === 'beta'
              ? 'You are on the Beta Tester plan with unlimited access. After beta, choose a plan that fits your needs.'
              : 'Choose a plan that fits your production needs.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-3 mt-4">
          {displayTiers.map((tier) => {
            const isCurrent = tier.name === currentTierName
            return (
              <div
                key={tier.id}
                className={cn(
                  'rounded-lg border p-4 space-y-4',
                  isCurrent && 'border-primary ring-1 ring-primary',
                )}
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{tier.display_name}</h3>
                    {isCurrent && (
                      <Badge variant="secondary" className="text-xs">Current</Badge>
                    )}
                  </div>
                </div>

                <ul className="space-y-2 text-sm">
                  <LimitRow label="Projects" value={tier.max_projects} />
                  <LimitRow label="Total clips" value={tier.max_total_clips} />
                  <LimitRow label="Commits / project" value={tier.max_commits_per_project} />
                </ul>

                <Button
                  variant={isCurrent ? 'secondary' : 'default'}
                  size="sm"
                  className="w-full"
                  disabled={isCurrent}
                  onClick={() => {
                    window.open('mailto:support@aiontrack.com?subject=Plan%20Upgrade%20Request', '_blank')
                  }}
                >
                  {isCurrent ? 'Current Plan' : 'Contact Us'}
                </Button>
              </div>
            )
          })}
        </div>

        {currentTierName === 'beta' && (
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Beta testers have unlimited access. These limits will apply after beta ends.
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}

function LimitRow({ label, value }: { label: string; value: number | null }) {
  return (
    <li className="flex items-center gap-2">
      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
      <span className="whitespace-nowrap">
        <span className="font-medium">{formatLimit(value)}</span>{' '}
        <span className="text-muted-foreground">{label}</span>
      </span>
    </li>
  )
}
